import React, { useEffect, useMemo } from "react";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import styles from "./CreateTemplateModal.module.scss";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { useTranslation } from "react-i18next";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { CreateTemplateModalStore, WABA_TEMPLATE_BUTTON_TEXT_MAX_LENGTH } from "./CreateTemplateModalStore";
import { WhatsappBusinessApiHeaderType } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { TextArea } from "@whatslly/ui-components/v2/BaseElements/TextArea";
import { LanguagesEnum } from "@whatslly/types/tuvis-messaging/enums";
import { FileDropzone } from "@whatslly/ui-components/v2/FileDropzone/FileDropzone";
import { LuX } from "react-icons/lu";
import classNames from "classnames";
import { Spinner } from "@whatslly/ui-components/Loader";
import { getMediaAccept, MEDIA_HEADER_ICONS } from "@whatslly/messaging-ui/Pages/TuvisMessaging/agentUtils";
type WabaOption = { label: string; value: string };

interface CreateTemplateSubmitData {
  wabaId: string;
  name: string;
  language: string;
  category: string;
  components: any[];
}

interface CreateTemplateModalProps extends IModalInstance<CreateTemplateSubmitData> {
  wabaSelectOptions: WabaOption[];
  onUploadHeaderMedia?: (wabaId: string, file: File) => Promise<{ handle: string }>;
}

const MediaHeaderPreview: React.FC<{ mediaType: string; file: File | null }> = ({ mediaType, file }) => {
  const Icon = MEDIA_HEADER_ICONS[mediaType];
  const isImage = mediaType === "IMAGE";
  const previewUrl = useMemo(() => (file && isImage ? URL.createObjectURL(file) : null), [file, isImage]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div
      className={classNames(styles.mediaHeaderPreview, {
        [styles.hasMediaPreview]: file && isImage,
        [styles.hasTextPreview]: file && !isImage,
      })}
    >
      {isImage && previewUrl ? (
        <div className={styles.filePreview}>
          <img src={previewUrl} alt={file!.name} />
        </div>
      ) : (
        <div className={styles.fileDetails}>
          {Icon && <Icon size={16} />}
          {file && <div className={styles.fileDetailsText}>{file.name}</div>}
        </div>
      )}
    </div>
  );
};

const PhonePreview: React.FC<{ store: CreateTemplateModalStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  const { previewData } = store;
  const hasContent = previewData.headerText || previewData.headerMediaType || previewData.bodyTextWithSamples || previewData.footerText;

  return (
    <div className={styles.phoneFrame}>
      <div className={styles.phoneHeader}>
        <div className={styles.phoneAvatar}>W</div>
        <span>WhatsApp</span>
      </div>
      <div className={styles.chatArea}>
        {hasContent ? (
          <>
            <div className={styles.chatBubble}>
              {previewData.headerMediaType && <MediaHeaderPreview mediaType={previewData.headerMediaType} file={store.headerMediaFile} />}
              <div className={styles.bubbleContent}>
                {previewData.headerText && (
                  <div data-testid="create-template-preview-header" className={styles.bubbleHeader}>
                    {previewData.headerText}
                  </div>
                )}
                {previewData.bodyTextWithSamples && (
                  <div data-testid="create-template-preview-body" className={styles.bubbleBody}>
                    {previewData.bodyTextWithSamples}
                  </div>
                )}
                {previewData.footerText && (
                  <div data-testid="create-template-preview-footer" className={styles.bubbleFooter}>
                    {previewData.footerText}
                  </div>
                )}
                <div className={styles.bubbleTime}>12:00</div>
              </div>
            </div>
            {previewData.buttons.length > 0 && (
              <div className={styles.previewButtons}>
                {previewData.buttons.map((btn, i) => (
                  <div key={i} className={styles.previewButton}>
                    {btn.text || "Button"}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyPreview}>{t("whatsapp_business_api_settings_page.createTemplateModal.template_preview_placeholder")}</div>
        )}
      </div>
    </div>
  );
});

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = observer(({ closeModal, wabaSelectOptions, onUploadHeaderMedia }) => {
  const { t } = useTranslation();
  const localStore = useLocalStoreWithInjectedDependencies(CreateTemplateModalStore);
  const showWabaSelect = wabaSelectOptions.length > 1;

  const categoryOptions = [
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.category_utility"), value: "UTILITY" },
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.category_marketing"), value: "MARKETING" },
  ];

  const headerTypeOptions = [
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.header_none"), value: "NONE" },
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.header_text"), value: "TEXT" },
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.header_image"), value: "IMAGE" },
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.header_video"), value: "VIDEO" },
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.header_document"), value: "DOCUMENT" },
  ];

  const buttonTypeOptions = [
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.button_quick_reply"), value: "QUICK_REPLY" },
    { label: t("whatsapp_business_api_settings_page.createTemplateModal.button_url"), value: "URL" },
  ];

  const languageOptions = Object.values(LanguagesEnum).map((code) => ({
    label: t(`whatsapp_business_api_agent_page.template_langs.${code}`),
    value: code,
  }));

  useEffect(() => {
    if (wabaSelectOptions.length > 0) {
      localStore.setWabaOption(wabaSelectOptions[0]);
    }
  }, [localStore, wabaSelectOptions]);

  const handleMediaFileSelect = (_dataUrl: string, file: File) => {
    if (!onUploadHeaderMedia) {
      localStore.setHeaderMediaFile(file);
      return;
    }

    localStore.uploadHeaderMedia(onUploadHeaderMedia, file);
  };

  const uploadState = localStore.headerMediaUpload?.state;

  return (
    <BaseModal
      testId="create-template-modal"
      header={<ModalHeader closeModal={() => closeModal()}>{t("whatsapp_business_api_settings_page.createTemplateModal.title")}</ModalHeader>}
      footer={
        <ModalButtons
          isSubmitDisabled={!localStore.isValidInput}
          testId="create-template-submit-button"
          submitText={t("whatsapp_business_api_settings_page.createTemplateModal.submit")}
          onCancel={() => closeModal()}
          onSubmit={() => closeModal(localStore.submitData)}
        />
      }
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={() => closeModal()}
      className={styles.modal}
    >
      <div className={styles.content}>
        <div className={styles.formSection}>
          <div className={styles.fieldSection}>
            <div className={styles.fieldTitleRow}>
              <span>{t("whatsapp_business_api_settings_page.createTemplateModal.template_name")}</span>
              <Tooltip delay={300} value={t("whatsapp_business_api_settings_page.createTemplateModal.template_name_tooltip")} placement="right">
                <span className={styles.tooltipIcon}>&#9432;</span>
              </Tooltip>
            </div>
            <TextInput
              data-testid="create-template-name-input"
              paddingInline="none"
              placeholder={t("whatsapp_business_api_settings_page.createTemplateModal.template_name_placeholder")}
              value={localStore.name}
              onChange={(e) => localStore.setName(e.target.value)}
              onClear={() => localStore.setName("")}
            />
          </div>
          {showWabaSelect && (
            <>
              <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.createTemplateModal.waba_account")}</div>
              <Select
                options={wabaSelectOptions}
                value={localStore.wabaOption}
                onChange={(item: WabaOption | null) => item && localStore.setWabaOption(item)}
              />
            </>
          )}

          <div className={styles.inlineRow}>
            <div className={classNames(styles.inlineField, styles.fieldSection)}>
              <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.createTemplateModal.category")}</div>
              <Select
                data-testid="create-template-category-select"
                options={categoryOptions}
                value={categoryOptions.find((o) => o.value === localStore.category)}
                onChange={(item: { label: string; value: string } | null) => item && localStore.setCategory(item.value)}
              />
            </div>
            <div className={classNames(styles.inlineField, styles.fieldSection)}>
              <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.createTemplateModal.language")}</div>
              <Select
                data-testid="create-template-language-select"
                isSearchable
                options={languageOptions}
                value={languageOptions.find((o) => o.value === localStore.language)}
                onChange={(item: { label: string; value: string } | null) => item && localStore.setLanguage(item.value)}
              />
            </div>
          </div>

          <div className={styles.fieldSection}>
            <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.createTemplateModal.header_type")}</div>
            <div className={styles.headerTypeRow}>
              <Select
                data-testid="create-template-header-type-select"
                options={headerTypeOptions}
                value={headerTypeOptions.find((o) => o.value === localStore.headerType)}
                onChange={(item: { label: string; value: WhatsappBusinessApiHeaderType } | null) => item && localStore.setHeaderType(item.value)}
              />
            </div>

            {localStore.headerType === "TEXT" && (
              <TextInput
                data-testid="create-template-header-text-input"
                paddingInline="none"
                placeholder={t("whatsapp_business_api_settings_page.createTemplateModal.header_text_placeholder")}
                value={localStore.headerText}
                onChange={(e) => localStore.setHeaderText(e.target.value)}
                onClear={() => localStore.setHeaderText("")}
              />
            )}

            {localStore.isMediaHeaderType && (
              <div className={styles.mediaUploadSection}>
                {localStore.headerMediaFile ? (
                  <div className={styles.mediaUploadStatus}>
                    <div className={styles.mediaUploadStatusContent}>
                      <span className={styles.mediaFileName}>{localStore.headerMediaFile.name}</span>
                      {uploadState === "pending" && (
                        <span className={styles.uploadingText}>
                          <Spinner />
                        </span>
                      )}
                      {uploadState !== "pending" && (
                        <span className={styles.removeMediaBtn} onClick={() => localStore.clearHeaderMedia()}>
                          <LuX size={16} />
                        </span>
                      )}
                    </div>
                    {uploadState === "rejected" && (
                      <span className={styles.uploadErrorText}>
                        {t("whatsapp_business_api_settings_page.createTemplateModal.header_media_upload_failed")}
                      </span>
                    )}
                  </div>
                ) : (
                  <FileDropzone
                    mode="dataUrl"
                    className={styles.mediaDropzone}
                    accept={getMediaAccept(localStore.headerType)}
                    onFile={handleMediaFileSelect}
                  >
                    <div>{t("whatsapp_business_api_settings_page.createTemplateModal.header_media_upload")}</div>
                  </FileDropzone>
                )}
              </div>
            )}
          </div>

          <div className={styles.fieldSection}>
            <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.createTemplateModal.body")}</div>
            <TextArea
              data-testid="create-template-body-textarea"
              paddingInline="none"
              placeholder={t("whatsapp_business_api_settings_page.createTemplateModal.body_placeholder")}
              value={localStore.bodyText}
              onChange={(e) => localStore.setBodyText(e.target.value)}
            />
          </div>
          <div className={styles.helperText}>{t("whatsapp_business_api_settings_page.createTemplateModal.body_helper")}</div>

          {localStore.detectedParameters.length > 0 && (
            <div className={styles.parameterSamples}>
              {localStore.detectedParameters.map((n) => (
                <div key={n}>
                  <div className={styles.parameterLabel}>
                    {t("whatsapp_business_api_settings_page.createTemplateModal.parameter_sample", { param: `{{${n}}}` })}
                  </div>
                  <TextInput
                    data-testid={`create-template-param-${n}-input`}
                    paddingInline="none"
                    placeholder={t("whatsapp_business_api_settings_page.createTemplateModal.parameter_sample_placeholder")}
                    value={localStore.bodyParameterSamples[n] ?? ""}
                    onChange={(e) => localStore.setParameterSample(n, e.target.value)}
                    onClear={() => localStore.setParameterSample(n, "")}
                  />
                </div>
              ))}
            </div>
          )}

          <div className={styles.fieldSection}>
            <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.createTemplateModal.footer")}</div>
            <TextInput
              data-testid="create-template-footer-input"
              paddingInline="none"
              placeholder={t("whatsapp_business_api_settings_page.createTemplateModal.footer_placeholder")}
              value={localStore.footerText}
              onChange={(e) => localStore.setFooterText(e.target.value)}
              onClear={() => localStore.setFooterText("")}
            />
          </div>
          <div className={styles.fieldSection}>
            <div className={classNames(styles.fieldTitleWithAction, styles.fieldTitle)}>
              {t("whatsapp_business_api_settings_page.createTemplateModal.buttons")}
              {localStore.buttons.length < 3 ? (
                <span data-testid="create-template-add-button" className={styles.addButtonLink} onClick={() => localStore.addButton()}>
                  {t("whatsapp_business_api_settings_page.createTemplateModal.add_button")}
                </span>
              ) : (
                <span className={styles.maxButtonsText}>{t("whatsapp_business_api_settings_page.createTemplateModal.max_buttons_reached")}</span>
              )}
            </div>
            {localStore.buttons.map((btn, index) => (
              <div key={index} className={styles.buttonRow}>
                <div className={styles.buttonRowFields}>
                  <div className={styles.typeSelect}>
                    <Select
                      data-testid={`create-template-btn-type-${index}-select`}
                      options={buttonTypeOptions}
                      value={buttonTypeOptions.find((o) => o.value === btn.type)}
                      onChange={(item: { label: string; value: string } | null) => item && localStore.updateButton(index, "type", item.value)}
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div className={styles.buttonTextField}>
                    <TextInput
                      data-testid={`create-template-btn-text-${index}-input`}
                      paddingInline="none"
                      placeholder={t("whatsapp_business_api_settings_page.createTemplateModal.button_text_placeholder")}
                      value={btn.text}
                      onChange={(e) => localStore.updateButton(index, "text", e.target.value)}
                      onClear={() => localStore.updateButton(index, "text", "")}
                      borderColor={btn.text.length > WABA_TEMPLATE_BUTTON_TEXT_MAX_LENGTH ? "error-primary" : "neutral-secondary"}
                    />
                    {btn.text.length > WABA_TEMPLATE_BUTTON_TEXT_MAX_LENGTH && (
                      <div data-testid={`create-template-btn-text-${index}-error`} className={styles.errorText}>
                        {t("whatsapp_business_api_settings_page.createTemplateModal.button_text_too_long", {
                          max: WABA_TEMPLATE_BUTTON_TEXT_MAX_LENGTH,
                        })}
                      </div>
                    )}
                  </div>
                  {btn.type === "URL" && (
                    <TextInput
                      data-testid={`create-template-btn-url-${index}-input`}
                      paddingInline="none"
                      placeholder={t("whatsapp_business_api_settings_page.createTemplateModal.button_url_placeholder")}
                      value={btn.url}
                      onChange={(e) => localStore.updateButton(index, "url", e.target.value)}
                      onClear={() => localStore.updateButton(index, "url", "")}
                    />
                  )}
                </div>
                <span
                  data-testid={`create-template-remove-btn-${index}`}
                  className={styles.removeButton}
                  onClick={() => localStore.removeButton(index)}
                >
                  &times;
                </span>
              </div>
            ))}
            {localStore.buttons.length === 0 && (
              <div className={styles.noButtonsText}>{t("whatsapp_business_api_settings_page.createTemplateModal.no_buttons_text")}</div>
            )}
          </div>
        </div>

        <div className={styles.previewSection}>
          <PhonePreview store={localStore} />
        </div>
      </div>
    </BaseModal>
  );
});
